/**
 * Durable log shipping (2026-09-25 diagnostics).
 *
 * Ships a FILTERED subset of `logBuffer.ts`'s own console patch to
 * `POST /screens/:screenId/log-stream` (`DeviceLogEntryModel`'s own doc
 * comment has the full incident context) every `SHIP_INTERVAL_MS`, so a
 * multi-day unattended soak has a complete, queryable trail instead of
 * whatever happened to still be in the 500-line ring buffer when someone
 * next pulled a `log_capture`.
 *
 * Filtered rather than everything, on purpose: this device's own routine
 * platform housekeeping (`ProductInfo`/`network.js` polling) alone produces
 * enough volume to fill the ring buffer inside ~30 minutes - shipping all of
 * it for days would be pure noise at real storage cost. Kept: anything
 * tagged `[shaka]` or `[signage-engine:` (every diagnostic this investigation
 * has actually used), plus any warn/error regardless of tag, so nothing
 * unexpected is silently dropped.
 *
 * Deliberately best-effort and bounded on every axis: a network outage or a
 * server error must never grow unbounded memory, throw into the app, or
 * compete with playback for anything that matters.
 */
import { onLogLine, type LogLine } from "./logBuffer";
import { reportNativeExits } from "./nativeExits";
import { bootLifecycleLines } from "./pageLifecycle";

const SHIP_INTERVAL_MS = 20_000;
/** Dropped oldest-first past this - bounds memory during an extended outage
 *  rather than trying to guarantee delivery of everything ever logged. */
const MAX_QUEUED = 1000;
const RELEVANT = /\[shaka\]|\[signage-engine:|\[lifecycle\]/;

let queue: LogLine[] = [];
let unsubscribe: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let cancelExits: (() => void) | null = null;
/** The boot `[lifecycle]` lines are sent once per page, not once per start. */
let bootLinesSent = false;
let removePageHide: (() => void) | null = null;

function isRelevant(line: LogLine): boolean {
  return (
    line.level === "warn" ||
    line.level === "error" ||
    RELEVANT.test(line.message)
  );
}

async function flush(
  screenId: string,
  getScreenToken: () => string | undefined
): Promise<void> {
  if (queue.length === 0) return;
  const token = getScreenToken();
  if (!token) return; // not paired yet / lost binding - try again next tick
  const batch = queue;
  queue = [];
  try {
    const res = await fetch(`/api/foyer/screens/${screenId}/log-stream`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Screen-Token": token,
      },
      body: JSON.stringify({ entries: batch }),
    });
    if (!res.ok) {
      // Put it back (bounded) rather than lose it to one bad response -
      // a 5xx or a rate-limit hit is exactly the transient case worth retrying.
      requeue(batch);
    }
  } catch {
    // Offline/DNS/etc - same recovery as above, no console.* here (would
    // re-enter this exact pipeline).
    requeue(batch);
  }
}

/**
 * Keepalive requests share a ~64 KB in-flight budget per page, and a body
 * over it is refused outright. Stay well under it and keep the NEWEST lines,
 * which are the ones next to whatever is tearing the page down.
 */
const PAGEHIDE_BUDGET_BYTES = 48 * 1024;

/**
 * Last-chance send when the page is going away (reload, navigation, the
 * Tizen widget being relaunched). Without it, up to 20 s of lines queued
 * since the last tick die with the page, and those are exactly the lines
 * that explain the restart. Requested for the Samsung QB24C mid-screenshot
 * restart, 2026-09-28.
 *
 * Best effort by nature: `pagehide` does not fire when the renderer
 * crashes or the process is killed. Those are covered on Android by the
 * pushed exit reasons (nativeExits.ts).
 */
function flushOnPageHide(
  screenId: string,
  getScreenToken: () => string | undefined
): void {
  if (queue.length === 0) return;
  const token = getScreenToken();
  if (!token) return;
  const batch: LogLine[] = [];
  let bytes = 0;
  for (let i = queue.length - 1; i >= 0; i--) {
    const size = queue[i]!.message.length + 80;
    if (bytes + size > PAGEHIDE_BUDGET_BYTES) break;
    bytes += size;
    batch.unshift(queue[i]!);
  }
  queue = [];
  if (batch.length === 0) return;
  try {
    void fetch(`/api/foyer/screens/${screenId}/log-stream`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Screen-Token": token,
      },
      body: JSON.stringify({ entries: batch }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* the page is going away; nothing to recover into */
  }
}

function requeue(batch: LogLine[]): void {
  queue = [...batch, ...queue].slice(0, MAX_QUEUED);
}

/**
 * Start shipping. Safe to call once per app session; call the returned
 * function to stop (mirrors the heartbeat effect's own start/stop lifecycle
 * in App.tsx, which is where this is wired in).
 */
export function startLogShipper(
  screenId: string,
  getScreenToken: () => string | undefined
): () => void {
  stopLogShipper();
  // Logged at boot, before this shipper could exist (it needs a paired
  // screen): "previous page ended UNCLEANLY" must not be lost to that order.
  if (!bootLinesSent) {
    bootLinesSent = true;
    queue.push(...bootLifecycleLines());
  }
  unsubscribe = onLogLine((line) => {
    if (!isRelevant(line)) return;
    queue.push(line);
    if (queue.length > MAX_QUEUED) queue.shift();
  });
  timer = setInterval(() => {
    void flush(screenId, getScreenToken);
  }, SHIP_INTERVAL_MS);
  // Same lifecycle and the same identity, so it rides along here rather
  // than adding another effect to App.tsx: the Android shell's exit reasons
  // (crash / ANR / OOM / renderer death), posted once per page load.
  cancelExits = reportNativeExits(screenId, getScreenToken);
  if (typeof window !== "undefined") {
    const onHide = () => flushOnPageHide(screenId, getScreenToken);
    window.addEventListener("pagehide", onHide);
    removePageHide = () => window.removeEventListener("pagehide", onHide);
  }
  return stopLogShipper;
}

export function stopLogShipper(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (cancelExits) {
    cancelExits();
    cancelExits = null;
  }
  if (removePageHide) {
    removePageHide();
    removePageHide = null;
  }
  queue = [];
}

/** Test-only: current queue depth, without exposing the queue itself. */
export function _queuedCount(): number {
  return queue.length;
}

/** Test-only: let the next start seed the boot `[lifecycle]` lines again. */
export function _resetBootLinesSentForTests(): void {
  bootLinesSent = false;
}
